import { fetchJSONFactory } from "@/utils/fetch-json";

const fetchJSON = fetchJSONFactory(import.meta.env.VITE_BOOKING_API);

type AppointmentService = {
  name: string;
  id: number;
  cancel_requires_payment: boolean;
};

type AppointmentType = {
  category: string;
  services: AppointmentService[];
};

export type FetchServicesResponse = AppointmentType[];

export function fetchServices(storeCode: string) {
  return fetchJSON<FetchServicesResponse>(`booking/store_services`, {
    params: { store_code: storeCode },
  });
}

export type Store = {
  store_code: string;
  is_pow: true;
  description: string;
  email: string;
  phone: string;
  location: {
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  };
  coordinates: {
    longitude: number;
    latitude: number;
  };
};

export type FetchStoreResponse = Store;

export function fetchStore(storeCode: string) {
  return fetchJSON<FetchStoreResponse>(`stores/store`, {
    params: { store_code: storeCode },
  });
}

type AvailableTimesItem = {
  appointment_datetime: string;
  display_time: string;
  group: "Morning" | "Afternoon" | "Evening";
};

export type FetchAvailableTimesResponse = {
  store: number;
  service: number;
  duration: number;
  times: AvailableTimesItem[];
};

export function fetchAvailableTimes(
  storeCode: string,
  serviceId: string | number,
  date: string,
) {
  return fetchJSON<FetchAvailableTimesResponse>(`booking/available_times`, {
    params: {
      store_code: storeCode,
      service: typeof serviceId === "number" ? serviceId.toString() : serviceId,
      date,
    },
  });
}

type BookAppointmentData = {
  storeCode: string;
  serviceId: string;
  time: string;
  notes: string;
};

export type BookAppointmentResponse = {
  service_name: string;
  appointment: number;
  start: string;
  start_datetime_tz: string;
  duration: number;
  store: {
    id: number;
    name: string;
    email: string;
    phone: string;
    location: { address: string; city: string; state: string; zip: string };
    tz: string;
  };
};

type BookAppointmentResult =
  | {
      type: "success";
      appointment: BookAppointmentResponse;
    }
  | {
      type: "noSlotsAvailable";
      params: {
        storeCode: string;
        serviceId: number;
        notes: string;
      };
    }
  | {
      type: "error";
      params: {
        storeCode: string;
        serviceId: number;
        notes: string;
      };
    };

export async function bookAppointment(
  params: BookAppointmentData,
): Promise<BookAppointmentResult> {
  const serviceId = Number(params.serviceId);
  try {
    const res = await fetchJSON<BookAppointmentResponse>(
      `booking/appointment`,
      {
        body: {
          notes: params.notes,
          store_code: params.storeCode,
          service_id: serviceId,
          time: params.time,
        },
        method: "POST",
      },
    );

    return {
      type: "success",
      appointment: res,
    };
  } catch (error) {
    if (error instanceof Error) {
      switch (error.message) {
        case "NO_SLOTS_AVAILABLE": {
          return {
            type: "noSlotsAvailable",
            params: {
              storeCode: params.storeCode,
              serviceId: serviceId,
              notes: params.notes,
            },
          };
        }
        default: {
          break;
        }
      }
    }

    return {
      type: "error",
      params: {
        storeCode: params.storeCode,
        serviceId: serviceId,
        notes: params.notes,
      },
    };
  }
}

export type Appointment = {
  id: number;
  start_datetime: string;
  start_datetime_tz: string;
  end_datetime: string;
  end_datetime_tz: string;
  duration: number;
  service_id: number;
  category: number;
  service_description: string;
  store_code: string;
  store_description: string;
  store_tz: string;
  is_first_time: boolean;
  is_cancelled: boolean;
  should_warn_before_cancel: boolean;
  canReschedule: boolean;
  warnBeforeReschedule: boolean;
  canConfirm: boolean;
  isConfirmed: boolean;
  date: string;
  canCancel: boolean;
};

export type UnsignedMembershipResponse = {
  id: number;
  description: string;
};

export type DashboardActions = {
  has_completed_wellness_plan: boolean;
  needs_waiver: boolean;
  unsignedMemberships: UnsignedMembershipResponse[];
};

export type FetchDashboardResponse = {
  upcoming: Appointment[];
  actions: DashboardActions;
  book_appointment_store: {
    store_code: string;
    is_pow: boolean;
    description: string;
    email: string;
    phone: string;
    location: {
      address: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    };
    coordinates: {
      longitude: number;
      latitude: number;
    };
    is_live: boolean;
    is_visible: boolean;
  };
};

export function fetchDashboard() {
  return fetchJSON<FetchDashboardResponse>(`dashboard/web`);
}

export type FetchFeaturesResponse = {
  wellnessPlan: true;
};

export function fetchFeatures() {
  return fetchJSON<FetchFeaturesResponse>("dashboard/feature-flags");
}

export type FetchServiceRecommendationsResponse = {
  name: string;
  service_id: number;
}[];

export function fetchServiceRecommendations() {
  return fetchJSON<FetchServiceRecommendationsResponse>(
    "booking/service_recommendations",
  );
}

type FetchAppointmentResult =
  | {
      type: "success";
      appointment: Appointment;
    }
  | {
      type: "error";
    }
  | {
      type: "notCustomersAppointment";
    };

export async function fetchAppointment(
  id?: string,
): Promise<FetchAppointmentResult> {
  try {
    const res = await fetchJSON<Appointment>(`booking/appointments/${id}`);
    return {
      type: "success",
      appointment: res,
    };
  } catch (e) {
    if (e instanceof Error && e.message === "UNAUTHORIZED_CUSTOMER") {
      return {
        type: "notCustomersAppointment",
      };
    }
    return {
      type: "error",
    };
  }
}

export function cancelAppointment(id: string) {
  return fetchJSON<BookAppointmentResponse>(
    `booking/appointments/${id}/cancel`,
    {
      method: "PUT",
    },
  );
}

type RescheduleAppointmentResult =
  | {
      type: "success";
      appointment: Appointment;
    }
  | {
      type: "timeTaken";
    }
  | {
      type: "error";
    };

export async function rescheduleAppointment(params: {
  appointmentId: number;
  time: string;
}): Promise<RescheduleAppointmentResult> {
  try {
    const res = await fetchJSON<Appointment>(
      "/booking/appointments/reschedule",
      {
        method: "PATCH",
        body: {
          appointmentId: params.appointmentId,
          time: params.time,
        },
      },
    );

    return {
      type: "success",
      appointment: res,
    };
  } catch (e) {
    if (e instanceof Error && e.message === "TIME_TAKEN") {
      return {
        type: "timeTaken",
      };
    }
    return {
      type: "error",
    };
  }
}

export type ConfirmAppointmentResult =
  | {
      type: "success";
      appointments: Appointment[];
    }
  | {
      type: "error";
    };

type ConfirmAppointmentResponse = {
  updatedAppointments: Appointment[];
};

export const confirmSingleAppointment = async (params: {
  appointmentId: number;
}): Promise<ConfirmAppointmentResult> => {
  try {
    const result = await fetchJSON<ConfirmAppointmentResponse>(
      `/booking/appointments/confirm`,
      {
        method: "PATCH",
        body: {
          appointmentId: params.appointmentId,
        },
      },
    );
    return { type: "success", appointments: result.updatedAppointments };
  } catch (e) {
    return { type: "error" };
  }
};

export const confirmAppointmentsForDate = async (params: {
  date: string;
}): Promise<ConfirmAppointmentResult> => {
  try {
    const result = await fetchJSON<ConfirmAppointmentResponse>(
      `/booking/appointments/confirm`,
      {
        method: "PATCH",
        body: {
          date: params.date,
        },
      },
    );
    return {
      type: "success",
      appointments: result.updatedAppointments,
    };
  } catch (e) {
    return {
      type: "error",
    };
  }
};
